// queue.view — daftar antrian per status + detail + dialog transisi. Tanpa akses DB/uang langsung.
import { isPiutangCara, wajibJatuhTempo, lunasFinal, billAmount } from '@domain/money';
import { caraBayarOpsi } from '@core/akunkas.cache';
import { rp, rpFmt, rpVal, esc, ageDays, isoOf, tglHuman, dtLocal, linkifyNos, wa62, fillTpl } from '@domain/format';
import { currentUser } from '@core/session';
import { bisaBuka } from '@core/perms';
import { openWA } from '@core/wa';
import { kirimKeKantor } from '@core/wa-kantor';
import { getTicketContact } from '@modules/intake/intake.repo';
import { canAdvance } from '@domain/status';
import { canMarkLunas } from '@domain/access';
import { garansiStatus, garansiDays } from '@domain/garansi';
import { jasaSellPrice } from '@domain/jasa';
import { slaInfo, fmtDurMin, type SlaResult } from '@domain/sla';
import { storageFeeAmount, readyDaysSince } from '@domain/storagefee';
import { kebijakan, profil, waTpl } from '@core/settings';
import { openModal } from '@ui/modal';
import { fotoUnitPicker, fotoUnitGrid, type FotoUnit } from '@ui/fotounit';
import { toast } from '@ui/toast';
import type { TicketStatus, Ticket } from '@core/types';
import { previewNota, previewTandaTerima, previewLabel, previewSuratKuasa, previewTracking } from '@modules/documents/documents.view';
import { alamatPublik, garansiBase } from '@modules/documents/documents.doc';
import { openRefundDialog } from '@modules/refund/refund.view';
import { openSparepartPanel } from '@modules/sparepart/sparepart.view';
import { refreshCounts } from '@core/nav';
import { requestDeleteTicket, ownerDeleteTicket, type ModeHapus } from '@modules/audit/audit.service';
import { ulangBadge } from './queue.tags';
import * as repo from './queue.repo';
import type { QueueRow } from './queue.repo';
import * as svc from './queue.service';

/** Buat render function untuk satu status (dipakai di menu shell). */
export const queueOf = (status: TicketStatus) => (root: HTMLElement): void => { void renderQueue(root, status); };

async function renderQueue(root: HTMLElement, status: string): Promise<void> {
  root.innerHTML = `<div class="crumb">Antrian</div><div class="ttl">${esc(status)}</div><div class="panel"><div class="empty">Memuat…</div></div>`;
  let rows: Awaited<ReturnType<typeof repo.listByStatus>>;
  try { rows = await repo.listByStatus(status); }
  catch (e) { root.innerHTML = `<div class="ttl">${esc(status)}</div><div class="panel err">${esc((e as Error).message)}</div>`; return; }

  const prioBadge = (p: string): string => {
    const c: Record<string, string> = { P0: 'var(--danger)', P1: 'var(--warn)' };
    const col = c[p] ?? 'var(--muted)';
    return `<span class="pill" style="background:${col}22;color:${col}">${esc(p)}</span>`;
  };
  const ageTag = (iso: string): string => { const d = ageDays(iso); return `<span style="color:${d > 5 ? 'var(--warn)' : 'var(--muted)'}">${d} hr</span>`; };

  // ===== Badge SLA per baris (SOP §5) =====
  const k = kebijakan();
  let enterMap = new Map<string, string>();
  try { enterMap = await repo.statusEnterMap(rows.map((r) => r.no), status); } catch { /* badge SLA opsional */ }
  const slaBadge = (r: QueueRow): string => {
    const at = enterMap.get(r.no);
    const s = slaInfo(status, at ? new Date(at).getTime() : NaN, r.estSelesai, k.slaOn, k.slaCfg);
    if (!s.applies) return '';
    if (s.breached) return ` <span class="pill" title="Melewati SLA" style="background:var(--dangerbg);color:var(--danger);font-size:10px">⏰ Lewat ${fmtDurMin(s.overMin ?? 0)}</span>`;
    if (s.ratio != null && s.ratio >= 0.8) return ` <span class="pill" title="Mendekati batas SLA" style="background:var(--warnbg);color:var(--warn);font-size:10px">⏱ ${fmtDurMin(Math.max(0, s.leftMin ?? 0))} lagi</span>`;
    return '';
  };

  // Badge "⏳ Belum diambil N hari" + biaya simpan (hanya status Siap Diambil). Port belumDiambilInfo v0.18 (baris 1520).
  const belumBadge = (r: QueueRow): string => {
    if (status !== 'Siap Diambil') return '';
    const d = ageDays(r.tglISO);
    if (d <= k.ambangBelum) return '';
    let msg = 'Belum diambil ' + d + ' hari';
    if (d > 180) msg += ' · ⚠️ &gt;6 bln: di luar tanggung jawab';
    else {
      const bs = storageFeeAmount(readyDaysSince(enterMap.get(r.no) ?? null), k.biayaSimpanBulan, k.biayaSimpanOn, { mulaiHari: k.simpanMulaiHari, lepasHari: k.simpanLepasHari, intervalHari: k.simpanIntervalHari });
      if (bs > 0) msg += ' · biaya simpan ' + rp(bs);
    }
    return `<div style="font-size:10px;color:var(--danger);font-weight:600;margin-top:2px">⏳ ${msg}</div>`;
  };

  // Kolom & badge status per jenis antrian (port renderStatusQueue v0.18).
  const isKonf = status === 'Menunggu Persetujuan', isKerja = status === 'Dikerjakan';
  const hasStatusCol = isKonf || isKerja;
  const shortcuts = !['Diagnosa', 'Dikerjakan'].includes(status); // Print Tanda + Tracking hanya bila relevan
  const fmtD = (iso: string | null | undefined): string => (iso ? tglHuman(new Date(iso)) : '—');
  const kerjaBadge = (kerja: string | null): string => {
    const m: Record<string, [string, string, string]> = {
      belum: ['Belum', '#eef2f6', 'var(--muted)'], proses: ['Proses', '#e7f1fb', '#1FA9E0'], hold: ['Hold', 'var(--warnbg)', 'var(--warn)'],
    };
    const x = m[kerja ?? 'belum'] ?? m['belum']!;
    return `<span class="pill" style="background:${x[1]};color:${x[2]}">${x[0]}</span>`;
  };
  const konfBadge = (k: boolean): string => k
    ? '<span class="pill" style="background:var(--okbg);color:var(--ok)">✔ Sudah konfirmasi</span>'
    : '<span class="pill" style="background:var(--warnbg);color:var(--warn)">Belum konfirmasi</span>';
  const statusCell = (r: QueueRow): string => {
    if (isKonf) return `<td>${konfBadge(r.konfirmasi)}${r.reconfirm ? ' <span class="pill" style="background:var(--warnbg);color:var(--warn)">🔁 Ulang</span>' : ''}</td>`;
    if (isKerja) return `<td>${kerjaBadge(r.kerja)}</td>`;   // penanda servis-ulang kini di kolom No. Servis (satu tempat, semua antrian)
    return '';
  };
  // aria-label per baris menyebut NOMOR tiketnya — "Detail" saja tak berarti apa-apa saat
  // pembaca layar melompati 20 baris yang tombolnya sama persis.
  const rowActions = (r: QueueRow): string =>
    `<button class="ico-btn" title="Detail & proses" aria-label="Detail & proses ${esc(r.no)}" data-act="detail" data-no="${esc(r.no)}">🔍</button>` +
    (shortcuts ? `<button class="ico-btn" title="Print Tanda Terima" aria-label="Print Tanda Terima ${esc(r.no)}" data-act="tanda" data-no="${esc(r.no)}">🖨️</button><button class="ico-btn" title="Kirim ulang link tracking via WhatsApp" aria-label="Kirim ulang link tracking ${esc(r.no)}" data-act="track" data-no="${esc(r.no)}">🔗</button>` : '');

  const table = rows.length
    ? `<table class="data"><thead><tr><th>No. Servis</th><th>Tgl Terima</th><th>Pelanggan</th><th>Unit</th><th>Kerusakan</th><th>Teknisi</th>${hasStatusCol ? '<th>Status</th>' : ''}<th>Prio</th><th>Umur</th><th>Aksi</th></tr></thead><tbody>${
        rows.map((r) => `<tr${isKerja ? ` data-kerja="${esc(r.kerja ?? 'belum')}"` : ''}${isKonf ? ` data-konf="${r.reconfirm ? 'ulang' : (r.konfirmasi ? 'sudah' : 'belum')}"` : ''}><td><b>${esc(r.no)}</b>${belumBadge(r)}${ulangBadge(r.servisUlang, r.priorRef)}</td><td>${fmtD(r.tglISO)}</td><td>${esc(r.custNama)}</td><td>${esc(r.unit)}${(!isKerja && r.hold) ? ' <span class="pill" style="background:var(--warnbg);color:var(--warn)">⏸️ Sparepart</span>' : ''}</td><td>${esc(r.keluhan)}</td><td>${esc(r.teknisi ?? '—')}</td>${statusCell(r)}<td>${prioBadge(r.prioritas)}</td><td>${ageTag(r.tglISO)}${slaBadge(r)}</td><td style="white-space:nowrap">${rowActions(r)}</td></tr>`).join('')
      }</tbody></table>`
    : `<div class="panel"><div class="empty">Tidak ada tiket berstatus "${esc(status)}".</div></div>`;

  const kerjaFilter = isKerja
    ? `<select id="q_kerja">${[['', 'Semua status kerja'], ['belum', 'Belum dikerjakan'], ['proses', 'Dalam pengerjaan'], ['hold', 'Hold']].map(([v, l]) => `<option value="${v}">${l}</option>`).join('')}</select>`
    : '';
  const konfFilter = isKonf
    ? `<select id="q_konf">${[['', 'Semua konfirmasi'], ['belum', 'Belum konfirmasi'], ['sudah', 'Sudah konfirmasi'], ['ulang', 'Konfirmasi ulang']].map(([v, l]) => `<option value="${v}">${l}</option>`).join('')}</select>`
    : '';

  root.innerHTML = `<div class="crumb">Antrian · ${esc(currentUser()?.role ?? '')}</div>
    <div class="ttl">${esc(status)} <span class="pill" style="background:var(--ice);color:var(--navy)" id="q_count">${rows.length}</span></div>
    <div class="scope-note">Klik 🔍 untuk melihat & memproses tiket.</div>
    ${rows.length ? `<div class="toolbar"><input id="q_search" placeholder="Cari no / pelanggan / unit…" aria-label="Cari tiket" style="flex:1;min-width:200px">${kerjaFilter}${konfFilter}</div>` : ''}${table}
    <div class="panel" id="q_nomatch" style="display:none"><div class="empty">Tak ada tiket cocok dengan pencarian/filter ini. <button class="btn" id="q_clear" style="padding:3px 10px;margin-left:6px">Bersihkan filter</button></div></div>`;

  const applyFilter = (): void => {
    const q = (root.querySelector('#q_search') as HTMLInputElement | null)?.value.trim().toLowerCase() ?? '';
    const kf = (root.querySelector('#q_kerja') as HTMLSelectElement | null)?.value ?? '';
    const cf = (root.querySelector('#q_konf') as HTMLSelectElement | null)?.value ?? '';
    let tampil = 0;
    root.querySelectorAll<HTMLElement>('tbody tr').forEach((tr) => {
      const okQ = !q || (tr.textContent ?? '').toLowerCase().includes(q);
      const okK = !kf || tr.dataset.kerja === kf;
      const okC = !cf || tr.dataset.konf === cf;
      const tampilkan = okQ && okK && okC;
      // Kontrak pgHide (ui-kit/table): filter hanya MENANDAI; paginator yang menulis display.
      if (tampilkan) tr.dataset['pgHide'] = ''; else tr.dataset['pgHide'] = '1';
      if (tampilkan) tampil++;
    });
    const tblPg = root.querySelector('table.data') as (HTMLTableElement & { __repage?: () => void }) | null;
    if (tblPg?.__repage) tblPg.__repage();
    else root.querySelectorAll<HTMLElement>('tbody tr').forEach((tr) => { tr.style.display = tr.dataset['pgHide'] === '1' ? 'none' : ''; }); // tanpa paginator (≤batas / 0): perilaku lama
    // Angka di judul ikut hasil filter — sebelumnya selalu total, jadi menyesatkan saat menyaring.
    const pill = root.querySelector('#q_count');
    if (pill) pill.textContent = (q || kf || cf) ? `${tampil} / ${rows.length}` : String(rows.length);
    // Semua baris tersembunyi → dulu layar KOSONG tanpa pesan (tampak seperti aplikasi rusak).
    const kosong = rows.length > 0 && tampil === 0;
    const nomatch = root.querySelector<HTMLElement>('#q_nomatch');
    if (nomatch) nomatch.style.display = kosong ? '' : 'none';
    // Sembunyikan TABELNYA, bukan pembungkusnya: pembungkus dipasang belakangan oleh observer, jadi
    // menyasar parent berisiko (sebelum dibungkus = wadah menu, sesudah = wrapper) dan bisa membuat
    // display:none nyangkut di elemen yang salah. Wrapper tanpa isi ikut kolaps 0px — tampilan sama.
    const tbl = root.querySelector<HTMLElement>('table.data');
    if (tbl) tbl.style.display = kosong ? 'none' : '';
  };
  root.querySelector('#q_clear')?.addEventListener('click', () => {
    const s = root.querySelector<HTMLInputElement>('#q_search'); if (s) s.value = '';
    root.querySelectorAll<HTMLSelectElement>('#q_kerja, #q_konf').forEach((el) => { el.value = ''; });
    applyFilter();
  });
  root.querySelector('#q_search')?.addEventListener('input', applyFilter);
  root.querySelector('#q_kerja')?.addEventListener('change', applyFilter);
  root.querySelector('#q_konf')?.addEventListener('change', applyFilter);

  const refresh = (): void => { void renderQueue(root, status); };
  root.querySelectorAll<HTMLElement>('button[data-act]').forEach((b) => b.addEventListener('click', () => {
    const no = b.dataset.no ?? '';
    switch (b.dataset.act) {
      case 'detail': void openDetail(no, refresh); break;
      case 'tanda': void previewTandaTerima(no); break;
      case 'track': void resendTracking(no); break; // B3: 🔗 = kirim ulang link tracking via WA (bukan print)
    }
  }));
}

/**
 * Kirim kartu status servis ke pelanggan (ikon 🔗 di semua antrian).
 * Lewat `previewTracking` — bukan mengirim teks `ifics.app/t/…` yang domainnya belum ada:
 * kartu itulah yang memuat status, QR, dan S&K yang diatur Apo, dan tautannya hidup
 * (disimpan lalu dikirim sebagai tautan bertanda-tangan).
 */
async function resendTracking(no: string): Promise<void> {
  await previewTracking(no);
}

const ACT_LABEL: Record<string, string> = {
  'Diterima': 'Tugaskan Teknisi →', 'Diagnosa': 'Selesai Diagnosa →',
  'Menunggu Persetujuan': 'Konfirmasi Pelanggan →', 'Dikerjakan': 'Hasil Pengerjaan →',
  'Siap Diambil': 'Serah Terima →',
};

/** Teks detail SLA di modal tiket. Port slaDetailText v0.18 (baris 491–494). */
function slaDetailHTML(status: string, s: SlaResult, estSelesai: string | null): string {
  if (!s.applies) return '<span style="color:var(--muted)">— (tidak ada target SLA pada status ini)</span>';
  const tgt = (status === 'Dikerjakan' && estSelesai) ? ('estimasi selesai ' + esc(estSelesai)) : (s.min ? fmtDurMin(s.min) : '—');
  if (s.breached) return `<b style="color:var(--danger)">⏰ Lewat SLA ${fmtDurMin(s.overMin ?? 0)}</b> · target ${tgt}`;
  return `<span style="color:var(--ok)">✔ Dalam SLA</span> · sisa ${fmtDurMin(Math.max(0, s.leftMin ?? 0))} · target ${tgt}`;
}

/**
 * Beri tahu SEBELUM petugas mengetik: unit ini sudah punya servis berjalan.
 * Layanan tetap menolak (dan DB menjaga) — ini hanya supaya penolakannya tidak muncul
 * setelah keluhan panjang terlanjur diketik.
 */
async function sudahAdaLanjutan(no: string, done: () => void): Promise<boolean> {
  let l: { no: string; status: string } | null = null;
  try { l = await repo.activeForUnit(no); } catch { return false; }   // gagal cek → biar layanan yang memutuskan
  if (!l) return false;
  if (confirm(`Unit ini sudah punya servis berjalan: ${l.no} (${l.status}).\n\nJangan buat tiket baru — lanjutkan yang itu.\n\nBuka tiket ${l.no} sekarang?`)) {
    void openDetail(l.no, done);
  }
  return true;
}

/** Alur "Servis Lagi (Garansi)" mandiri — dipakai baris Siap/Sudah Diambil. */
export async function runServisLagi(no: string, done: () => void): Promise<void> {
  let full: Awaited<ReturnType<typeof repo.getTicket>>;
  try { full = await repo.getTicket(no); } catch (e) { toast((e as Error).message); return; }
  const t = full.ticket;
  const garansi = t['garansi'] ? String(t['garansi']) : null;
  const garansiBase = t['garansi_base'] ? String(t['garansi_base']) : null;
  const outlet = String(t['outlet']);
  const gs = garansiStatus(garansi, garansiBase);
  if (!gs.aktif && await sudahAdaLanjutan(no, done)) return;
  const keluhan = prompt(gs.aktif ? 'Keluhan / klaim garansi:' : 'Garansi habis → tiket BARU (berbayar). Keluhan:');
  if (keluhan == null) return;
  try {
    const r = await svc.servisLagi(no, garansi, garansiBase, outlet, keluhan.trim() || '-');
    toast(r.mode === 'ulang' ? '🔄 Servis ulang dibuat (Dikerjakan)' : `🆕 Tiket baru berbayar: ${r.newNo}`);
    done(); refreshCounts();
  } catch (e) { toast((e as Error).message); }
}

/**
 * HAPUS TIKET — khusus Owner (Apo 28 Jul). Dua pilihan yang bedanya nyata bagi pembukuan:
 *  · Pertahankan data keuangan → tiket lenyap dari layar kerja, tapi omzet/piutang/survei yang sudah
 *    tercatat TIDAK berubah surut (dan tak nongol di Arsip — bagi pengguna ia sudah terhapus).
 *  · Hapus semua → baris & turunannya benar-benar hilang; DB menolak bila pembayarannya sudah
 *    diverifikasi Finance, supaya uang yang sudah diakui tak lenyap tanpa angka.
 * Password diminta ulang: kalau perangkat ditinggal dalam keadaan login, ini pagar terakhirnya.
 */
function dlgHapusOwner(no: string, refresh: () => void): void {
  const bg = openModal(`<div class="modal-head"><h3>🗑️ Hapus Transaksi ${esc(no)}</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="scope-note" style="background:var(--dangerbg);color:var(--danger)"><b>Tidak bisa dibatalkan.</b> Riwayat penghapusan tetap tercatat di Audit Log.</div>
      <div class="field"><label>Pilih cara hapus</label>
        <label style="display:flex;gap:8px;align-items:flex-start;margin:6px 0"><input type="radio" name="hp_mode" value="KEEP_FIN" checked>
          <span><b>Pertahankan data keuangan</b><br><span style="color:var(--muted);font-size:12px">Tiket hilang dari semua menu kerja, tapi omzet/piutang/survei yang sudah tercatat tetap utuh. Aman untuk transaksi yang sudah dibayar.</span></span></label>
        <label style="display:flex;gap:8px;align-items:flex-start;margin:6px 0"><input type="radio" name="hp_mode" value="TOTAL">
          <span><b>Hapus semua data terkait</b><br><span style="color:var(--muted);font-size:12px">Tiket, pembayaran, sparepart, log & notifikasinya dihapus permanen. Hanya bisa bila pembayarannya <b>belum diverifikasi Finance</b>.</span></span></label></div>
      <div class="field"><label>Alasan <span class="req">*</span></label><input id="hp_alasan" placeholder="mis. tiket dobel / salah input"></div>
      <div class="field"><label>Password Anda <span class="req">*</span></label><input id="hp_pass" type="password" autocomplete="current-password"></div>
      <div class="err" id="hp_err"></div></div>
    <div class="modal-foot"><button class="btn" id="hp_batal">Batal</button>
      <button class="btn" id="hp_ok" style="background:var(--danger);color:#fff;border-color:var(--danger)">Hapus</button></div>`);
  bg.querySelector('#hp_batal')?.addEventListener('click', () => bg.remove());
  const ok = bg.querySelector('#hp_ok') as HTMLButtonElement;
  ok.addEventListener('click', async () => {
    const err = bg.querySelector('#hp_err') as HTMLElement;
    const mode = (bg.querySelector('input[name="hp_mode"]:checked') as HTMLInputElement).value as ModeHapus;
    const alasan = (bg.querySelector('#hp_alasan') as HTMLInputElement).value;
    const pass = (bg.querySelector('#hp_pass') as HTMLInputElement).value;
    err.textContent = ''; ok.disabled = true; ok.textContent = 'Menghapus…';
    try {
      const hasil = await ownerDeleteTicket(no, mode, alasan, pass);
      bg.remove();
      toast(hasil === 'TOTAL' ? '🗑️ Transaksi & seluruh datanya dihapus' : '🗑️ Transaksi dihapus (data keuangan dipertahankan)');
      refresh(); refreshCounts();
    } catch (e) { err.textContent = (e as Error).message; ok.disabled = false; ok.textContent = 'Hapus'; }
  });
}

export async function openDetail(no: string, refresh: () => void): Promise<void> {
  const u = currentUser();
  let full: Awaited<ReturnType<typeof repo.getTicket>>;
  try { full = await repo.getTicket(no); } catch (e) { toast((e as Error).message); return; }
  const t = full.ticket;
  const status = String(t['status']);
  const outlet = String(t['outlet']);
  const isOwn = t['teknisi'] === u?.nama;
  // Tiket ter-ARSIP/VOID (§26) = BEKU → detail read-only (tak ada aksi mutasi; cegah "ghost" servis-ulang pada archived).
  const frozen = !!t['archived'] || !!t['voided'];
  const canAct = !!u && canAdvance(status as TicketStatus, u.role, isOwn) && !!ACT_LABEL[status] && !frozen;
  const isDone = status === 'Sudah Diambil' || t['hasil'] === 'jadi';
  const isDikerjakan = status === 'Dikerjakan' && !frozen;
  const isHold = !!t['hold'];
  const canPrio = !!u && ['Owner', 'Manager', 'SPV'].includes(u.role) && status !== 'Sudah Diambil' && !frozen;
  const isOwner = currentUser()?.role === 'Owner';
  const canDel = !!u && ['Owner', 'Manager', 'SPV'].includes(u.role) && !frozen; // §26: min SPV boleh AJUKAN (bukan hard-delete)
  const canBatal = !!u && status === 'Diterima' && ['Owner', 'Manager', 'SPV', 'CS'].includes(u.role) && !frozen; // v18: batal-langsung HANYA dari Diterima (Diagnosa/Persetujuan punya jalur batal sendiri)
  const canKonf = status === 'Menunggu Persetujuan' && !t['konfirmasi'] && !!u && ['Owner', 'Manager', 'SPV', 'CS'].includes(u.role) && !frozen;
  const canArchive = status === 'Sudah Diambil' && u?.role === 'Owner' && !frozen;
  const canUlang = ['Siap Diambil', 'Sudah Diambil'].includes(status) && !!u && ['Owner', 'Manager', 'SPV', 'CS'].includes(u.role) && !frozen; // v18: Servis Lagi dari Siap (in-place) & Sudah (garansi)
  const canRefund = status === 'Siap Diambil' && (t['hasil'] === 'tidakbisa' || t['hasil'] === 'batal') && !!u && ['Owner', 'Manager', 'SPV', 'CS'].includes(u.role) && !frozen;
  const canKuasa = status === 'Siap Diambil' && !!u && ['Owner', 'Manager', 'SPV', 'CS'].includes(u.role) && !frozen;
  const canSparepart = status === 'Dikerjakan' && !!u && ['Owner', 'Manager', 'SPV', 'Teknisi'].includes(u.role) && !frozen; // B4: Sparepart hanya di Dikerjakan (dihapus dari Diagnosa, permintaan Apo)
  // F1: histori sparepart (read-only) di Bisa/Sudah Diambil. Peran operasional yang menangani part (Teknisi lihat tiket sendiri via inScope/RLS); CS dikecualikan (harga part sensitif). Sama data & tiket dgn panel Dikerjakan — tak buka info baru.
  const canSparepartHist = ['Siap Diambil', 'Sudah Diambil'].includes(status) && !!u && ['Owner', 'Manager', 'SPV', 'Admin Finance', 'Teknisi'].includes(u.role);
  // "Kembalikan Sparepart" (retur) utk tiket GAGAL/BATAL yang sudah Siap/Sudah Diambil — O/M/SPV/CS + Teknisi (tiket sendiri). Approval supplier tetap.
  const canReturPickup = ['Siap Diambil', 'Sudah Diambil'].includes(status) && (t['hasil'] === 'tidakbisa' || t['hasil'] === 'batal') && !!u
    && ['Owner', 'Manager', 'SPV', 'CS', 'Teknisi'].includes(u.role) && (u.role !== 'Teknisi' || t['teknisi'] === u.nama) && !frozen;
  const notFinal = status !== 'Sudah Diambil';
  // Edit garansi pasca-serah: O/M saja (jaga-jaga CS/teknisi lupa isi) — alasan wajib, tercatat.
  const canEditGar = status === 'Sudah Diambil' && !!u && ['Owner', 'Manager'].includes(u.role) && !frozen;
  const isDiterima = status === 'Diterima' && !frozen; // dok Tanda Terima/Tracking/Label HANYA di tahap Diterima (v18)
  const showNota = ['Siap Diambil', 'Sudah Diambil'].includes(status) && !frozen; // Nota: preview di Siap, cetak di Sudah
  const oldEst = Number(t['est_biaya'] ?? 0);
  const garansi = t['garansi'] ? String(t['garansi']) : null;
  const garansiBase = t['garansi_base'] ? String(t['garansi_base']) : null;
  const gs = garansiStatus(garansi, garansiBase);

  const rows: [string, string][] = [
    ['Pelanggan', full.custNama], ['Unit', String(t['unit'] ?? '—')], ['Keluhan', String(t['keluhan'] ?? '—')],
    ['Teknisi', String(t['teknisi'] ?? '—')], ['Estimasi', rp(Number(t['est_biaya'] ?? 0))],
    ['DP', rp(Number(t['dp'] ?? 0))], ['Prioritas', String(t['prioritas'] ?? 'P2')],
  ];
  const kel = Array.isArray(t['kelengkapan']) ? (t['kelengkapan'] as string[]).join(', ') : String(t['kelengkapan'] ?? '');
  if (kel) rows.push(['Kelengkapan', kel]);
  if (t['kondisi']) rows.push(['Keterangan', String(t['kondisi'])]);
  if (t['ket']) rows.push(['Hasil diagnosa', String(t['ket'])]);
  if (t['catatan']) rows.push(['Catatan internal', String(t['catatan'])]);
  if (status === 'Siap Diambil') {
    // `billAmount`: begitu teknisi memvonis (hasil terisi), biaya final yang berlaku — termasuk
    // Rp 0 untuk garansi/tidak-bisa-diperbaiki. Tanpa itu panel ini menyebut ESTIMASI LAMA ke
    // pelanggan sementara dialog serah-terima menagih Rp 0 (dua angka, satu tiket).
    const billed = billAmount({ hasil: (t['hasil'] as string | null) ?? undefined,
      biaya: Number(t['biaya'] ?? 0), estBiaya: Number(t['est_biaya'] ?? 0) } as Ticket);
    rows.push(['Sisa dibayar', rp(Math.max(0, billed - Number(t['dp'] ?? 0)))]);
  }
  if (status === 'Sudah Diambil') rows.push(['Garansi', gs.txt]);
  const info = rows.map(([k, v]) => `<div class="info-row"><span class="k">${esc(k)}</span><span>${esc(v)}</span></div>`).join('');
  const riwayat = full.rounds.length
    ? `<div class="ttl" style="font-size:14px;margin:12px 0 4px">🔁 Riwayat Servis (sesi sebelumnya)</div>${full.rounds.map((r) => `<div class="info-row"><span class="k">Sesi ${r.ronde}</span><span>${esc(r.tindakan ?? '-')} · ${rp(Number(r.biaya ?? 0))} · ${esc(r.garansi ?? '-')}${r.verified ? ' · Lunas✔' : ''}</span></div>`).join('')}`
    : '';
  // Terbaru DI ATAS (Apo 19 Jul) — hanya urutan tampil; data asli tetap kronologis (dipakai SLA enterLog).
  const logHtml = [...full.logs].reverse().map((l) => `<div class="log-item"><b>${esc(l.status)}</b> — ${linkifyNos(esc(l.catatan))}<div class="lt">${esc(l.by_user)} · ${dtLocal(l.at)}</div></div>`).join('') || '<div class="empty">—</div>';

  // ===== Baris SLA (SOP §5) — hanya status operasional (bukan Sudah Diambil) =====
  const kbj = kebijakan();
  const enterLog = [...full.logs].reverse().find((l) => l.status === status);
  const estSel = t['est_selesai'] ? String(t['est_selesai']) : null;
  const slaInf = slaInfo(status, enterLog ? new Date(enterLog.at).getTime() : NaN, estSel, kbj.slaOn, kbj.slaCfg);
  const slaRow = notFinal ? `<div class="info-row"><span class="k">SLA</span><span>${slaDetailHTML(status, slaInf, estSel)}</span></div>` : '';

  const bg = openModal(`
    <div class="modal-head"><h3>${esc(no)} · ${esc(status)}</h3><button class="x">×</button></div>
    <div class="modal-body">${frozen ? `<div class="scope-note" style="background:var(--dangerbg);color:var(--danger);border-radius:8px;padding:6px 10px;margin-bottom:8px">🗄️ ${t['voided'] ? 'Transaksi di-VOID' : 'Terarsip'} — hanya-baca. Kembalikan dulu dari menu Arsip untuk mengubah.</div>` : ''}${info}<div id="qd_fotounit"></div>${slaRow}${riwayat}
      <div class="ttl" style="font-size:14px;margin:12px 0 4px">Log</div>${logHtml}</div>
    <div class="modal-foot" style="flex-wrap:wrap">
      ${showNota ? `<button class="btn" id="d_nota">🧾 Nota${status === 'Siap Diambil' ? ' (preview)' : ''}</button>` : ''}
      ${isDiterima ? `<button class="btn" id="d_tanda">📄 Tanda Terima</button>` : ''}
      ${isDiterima ? `<button class="btn" id="d_track">🔗 Kirim link tracking</button>` : ''}
      ${isDiterima ? `<button class="btn" id="d_label">🏷️ Label</button>` : ''}
      ${isDiterima ? `<button class="btn" id="d_wa">💬 Kirim WA (terima+lacak)</button>` : ''}
      ${['Diagnosa', 'Menunggu Persetujuan'].includes(status) ? `<button class="btn" id="d_wacs">💬 WA ke CS</button>` : ''}
      ${['Menunggu Persetujuan', 'Dikerjakan'].includes(status) ? `<button class="btn" id="d_watek">💬 WA ke Teknisi</button>` : ''}
      ${status === 'Sudah Diambil' ? `<button class="btn" id="d_wafin">💬 WA ke Finance</button>` : ''}
      ${canKuasa ? `<button class="btn" id="d_kuasa">📝 Diwakilkan</button>` : ''}
      ${canUlang ? `<button class="btn" id="d_ulang">🔄 Servis Lagi${status === 'Sudah Diambil' ? ' (Garansi)' : ''}</button>` : ''}
      ${isDikerjakan ? `<button class="btn" id="d_hold">${isHold ? '▶️ Lanjut' : '⏸️ Hold Sparepart'}</button>` : ''}
      ${canSparepart ? `<button class="btn" id="d_sparepart">🔩 Sparepart</button>` : ''}
      ${canSparepartHist ? `<button class="btn" id="d_spare_hist">🔩 Riwayat Sparepart</button>` : ''}
      ${canReturPickup ? `<button class="btn" id="d_kembalikan" style="border-color:var(--warn);color:var(--warn)">↩️ Kembalikan Sparepart</button>` : ''}
      ${isDikerjakan ? `<button class="btn" id="d_reconf">🔁 Konfirmasi Ulang</button>` : ''}
      ${canKonf ? `<button class="btn" id="d_konf">✅ Tandai sudah konfirmasi</button>` : ''}
      ${canPrio ? `<button class="btn" id="d_prio">⚡ Prioritas</button>` : ''}
      ${canBatal ? `<button class="btn" style="border-color:var(--warn);color:var(--warn)" id="d_batal">❌ Batalkan</button>` : ''}
      ${canRefund ? `<button class="btn" style="background:var(--danger);color:#fff;border-color:var(--danger)" id="d_refund">💸 Pengembalian Dana</button>` : ''}
      ${canEditGar ? `<button class="btn" id="d_gar">✏️ Ubah Garansi</button>` : ''}
      ${canArchive ? `<button class="btn" id="d_arsip">🗄️ Arsipkan</button>` : ''}
      ${canDel ? `<button class="btn" style="border-color:var(--danger);color:var(--danger)" id="d_del">🗑️ ${isOwner ? 'Hapus' : 'Ajukan Hapus'}</button>` : ''}
      ${canAct ? `<button class="btn primary" id="d_act">${esc(ACT_LABEL[status] ?? '')}</button>` : ''}
      <button class="btn" id="d_close">Tutup</button>
    </div>`);
  // Foto kondisi unit (bila ada) — bukti fisik saat diterima & diserahkan.
  void fotoUnitGrid(bg.querySelector('#qd_fotounit') as HTMLElement, ((t['foto_unit'] as FotoUnit[] | null) ?? []));
  const done = (): void => { bg.remove(); refresh(); refreshCounts(); };
  const reDetail = (): void => void openDetail(no, refresh); // ← Back tiap sub-dialog → buka lagi detail
  (bg.querySelector('#d_close') as HTMLElement).addEventListener('click', () => bg.remove());
  bg.querySelector('#d_nota')?.addEventListener('click', () => void previewNota(no));
  bg.querySelector('#d_tanda')?.addEventListener('click', () => void previewTandaTerima(no));
  bg.querySelector('#d_track')?.addEventListener('click', () => void resendTracking(no)); // B3: WA-resend link (bukan print)
  bg.querySelector('#d_label')?.addEventListener('click', () => void previewLabel(no));
  bg.querySelector('#d_wa')?.addEventListener('click', () => void (async () => { // D1: kirim ulang WA terima + link lacak (openWA=dummy di dev)
    try { const c = await getTicketContact(no); if (!c.wa) { toast('Nomor WA pelanggan belum ada.'); return; } openWA(c.wa, fillTpl(waTpl().terima, { nama: c.nama, no, unit: c.unit, merk: c.merk, tipe: c.tipe, kerusakan: c.kerusakan, tracking: `https://ifics.app/t/${c.token}` })); }
    catch (e) { toast((e as Error).message); }
  })());
  // Pesan internal: seluruh isinya dibentuk dari DATA TIKET — staf tak mengetik ulang apa pun,
  // jadi tak ada salah ketik nomor/nama/tipe. Tujuannya nomor kantor cabang tiket ini.
  const isiAtau = (v: unknown): string => String(v ?? '').trim() || '(belum diisi)';
  const dataTiket = (): Record<string, string> => ({
    no, pelanggan: full.custNama, merk: String(t['merk'] ?? ''), tipe: String(t['model'] ?? ''),
    kerusakan: String(t['keluhan'] ?? ''), ket: isiAtau(t['ket']),
    est: Number(t['est_biaya'] ?? 0) || Number(t['biaya'] ?? 0) ? rp(Number(t['est_biaya'] ?? 0) || Number(t['biaya'] ?? 0)) : '(belum diisi)',
    lama: isiAtau(t['lama'] ?? t['est_selesai']), teknisi: isiAtau(t['teknisi']),
    cabang: outlet, jumlah: rp(Number(t['biaya'] ?? 0)), cara: String(t['cara_bayar'] ?? '—'),
    perihal: t['lunas'] ? 'Verifikasi pembayaran' : 'Tagihan belum lunas',
  });
  bg.querySelector('#d_wacs')?.addEventListener('click', () => kirimKeKantor(outlet, 'cs', fillTpl(waTpl().keCs, dataTiket()), toast));
  bg.querySelector('#d_watek')?.addEventListener('click', () => kirimKeKantor(outlet, 'teknisi', fillTpl(waTpl().keTeknisi, dataTiket()), toast));
  bg.querySelector('#d_wafin')?.addEventListener('click', () => kirimKeKantor(outlet, 'finance', fillTpl(waTpl().keFinance, dataTiket()), toast));
  bg.querySelector('#d_kuasa')?.addEventListener('click', () => { bg.remove(); void previewSuratKuasa(no, reDetail); });
  bg.querySelector('#d_konf')?.addEventListener('click', async () => {
    try { await svc.setKonfirmasi(no, true); toast('✔ Ditandai sudah konfirmasi'); done(); } catch (e) { toast((e as Error).message); }
  });
  bg.querySelector('#d_act')?.addEventListener('click', () => { bg.remove(); void openTransition(no, status, outlet, Number(t['modal'] ?? 0), refresh, reDetail); });
  bg.querySelector('#d_hold')?.addEventListener('click', async () => {
    let reason = '';
    if (!isHold) { const r = prompt('Alasan hold (mis. menunggu sparepart):'); if (r === null) return; reason = r.trim(); }
    try { await svc.setHold(no, !isHold, reason); toast('✅ Diperbarui'); done(); } catch (e) { toast((e as Error).message); }
  });
  bg.querySelector('#d_sparepart')?.addEventListener('click', () => { bg.remove(); void openSparepartPanel(no, outlet, refresh, false, false, reDetail); });
  bg.querySelector('#d_spare_hist')?.addEventListener('click', () => { bg.remove(); void openSparepartPanel(no, outlet, () => {}, true, false, reDetail); }); // F1: read-only histori
  bg.querySelector('#d_kembalikan')?.addEventListener('click', () => { bg.remove(); void openSparepartPanel(no, outlet, refresh, true, true, reDetail); }); // returOnly: kembalikan part tiket gagal/batal
  bg.querySelector('#d_reconf')?.addEventListener('click', () => { bg.remove(); dlgReconfirm(no, oldEst, refresh, reDetail); });
  bg.querySelector('#d_prio')?.addEventListener('click', () => { bg.remove(); dlgPrioritas(no, String(t['prioritas'] ?? 'P2'), refresh, reDetail); });
  bg.querySelector('#d_del')?.addEventListener('click', () => {
    // Owner = otoritas tertinggi → hapus langsung (dialog 2 pilihan + password). Peran lain mengajukan.
    if (isOwner) { bg.remove(); dlgHapusOwner(no, refresh); return; }
    void (async () => {
      const reason = prompt(`Ajukan ${isDone ? 'arsip/hapus (transaksi selesai → ARCHIVE)' : 'pembatalan (VOID)'} tiket ${no}.\nAlasan (wajib):`);
      if (reason == null) return;
      try { await requestDeleteTicket(no, reason.trim()); toast('🗳️ Permintaan dikirim — menunggu approval Owner'); bg.remove(); } catch (e) { toast((e as Error).message); }
    })();
  });
  bg.querySelector('#d_batal')?.addEventListener('click', async () => {
    const alasan = prompt('Alasan pembatalan:');
    if (alasan == null) return;
    try { await svc.batalLangsung(no, alasan.trim() || '-'); toast('✅ Dibatalkan'); done(); } catch (e) { toast((e as Error).message); }
  });
  bg.querySelector('#d_gar')?.addEventListener('click', () => { bg.remove(); dlgEditGaransi(no, garansi ?? 'Tidak Ada', refresh, reDetail); });
  bg.querySelector('#d_arsip')?.addEventListener('click', async () => {
    if (!confirm(`Arsipkan transaksi ${no}? Hilang dari daftar operasional (data tetap ada).`)) return;
    try { await svc.archiveTicket(no); toast('🗄️ Diarsipkan'); done(); } catch (e) { toast((e as Error).message); }
  });
  bg.querySelector('#d_refund')?.addEventListener('click', () => { bg.remove(); void openRefundDialog(no, done, reDetail); }); // ← Back → buka lagi detail
  bg.querySelector('#d_ulang')?.addEventListener('click', async () => {
    if (!gs.aktif && await sudahAdaLanjutan(no, done)) { bg.remove(); return; }
    const keluhan = prompt(gs.aktif ? 'Keluhan / klaim garansi:' : 'Garansi habis → tiket BARU (berbayar). Keluhan:');
    if (keluhan == null) return;
    try {
      const r = await svc.servisLagi(no, garansi, garansiBase, outlet, keluhan.trim() || '-');
      toast(r.mode === 'ulang' ? '🔄 Servis ulang dibuat (Dikerjakan)' : `🆕 Tiket baru berbayar: ${r.newNo}`);
      done();
    } catch (e) { toast((e as Error).message); }
  });
}

function dlgEditGaransi(no: string, cur: string, done: () => void, onBack?: () => void): void {
  const bg = openModal(`<div class="modal-head"><h3>✏️ Ubah Garansi — ${esc(no)}</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="scope-note">Untuk koreksi bila garansi lupa diisi saat serah terima. Masa garansi tetap dihitung dari <b>tanggal pengambilan</b>, bukan tanggal edit. Tercatat di histori + audit.</div>
      <div class="field"><label>Masa Garansi</label><select id="g_gar">${GARANSI_OPTS.map((g) => `<option ${g === cur ? 'selected' : ''}>${esc(g)}</option>`).join('')}</select></div>
      <div class="field"><label>Alasan <span class="req">*</span></label><textarea id="g_alasan" rows="2" placeholder="mis. CS lupa mengisi garansi saat serah terima"></textarea></div>
      <div class="err"></div></div>
    <div class="modal-foot"><button class="btn" id="c">Batal</button><button class="btn primary" id="s">Simpan</button></div>`, { onBack });
  wireSave(bg, async () => {
    const gar = (bg.querySelector('#g_gar') as HTMLSelectElement).value;
    const alasan = (bg.querySelector('#g_alasan') as HTMLTextAreaElement).value.trim();
    if (!alasan) throw new Error('Alasan wajib diisi.');
    await svc.editGaransi(no, gar, alasan);
  }, done);
}

function wireSave(bg: HTMLElement, action: () => Promise<void>, done: () => void): void {
  (bg.querySelector('#c') as HTMLElement | null)?.addEventListener('click', () => bg.remove());
  const s = bg.querySelector('#s') as HTMLButtonElement;
  s.addEventListener('click', async () => {
    s.disabled = true;
    try { await action(); toast('✅ Tersimpan'); bg.remove(); done(); refreshCounts(); }
    catch (e) { const err = bg.querySelector('.err'); if (err) err.textContent = (e as Error).message; s.disabled = false; }
  });
}
const moneyInput = (bg: HTMLElement, id: string): void => {
  const el = bg.querySelector('#' + id) as HTMLInputElement | null;
  if (el) el.addEventListener('input', () => { el.value = rpFmt(el.value); });
};
const foot = `<div class="modal-foot"><button class="btn" id="c">Batal</button><button class="btn primary" id="s">Simpan</button></div>`;

async function openTransition(no: string, status: string, outlet: string, curModal: number, done: () => void, onBack?: () => void): Promise<void> {
  if (status === 'Diterima') return dlgAssign(no, outlet, done, onBack);
  if (status === 'Diagnosa') return dlgDiagnose(no, done, onBack);
  if (status === 'Menunggu Persetujuan') return dlgApprove(no, done, onBack);
  if (status === 'Dikerjakan') return dlgFinish(no, curModal, outlet, done, onBack);
  if (status === 'Siap Diambil') return dlgPickup(no, done, onBack);
}

async function dlgAssign(no: string, outlet: string, done: () => void, onBack?: () => void): Promise<void> {
  const techs = await repo.listTechnicians(outlet);
  const bg = openModal(`<div class="modal-head"><h3>Tugaskan Teknisi</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="field"><label>Teknisi <span class="req">*</span></label><select id="a_tek">${techs.map((t) => `<option>${esc(t)}</option>`).join('') || '<option value="">(tak ada teknisi di cabang ini)</option>'}</select></div>
      <div class="field"><label>Catatan</label><input id="a_note"></div><div class="err"></div></div>${foot}`, { onBack });
  wireSave(bg, async () => {
    const tek = (bg.querySelector('#a_tek') as HTMLSelectElement).value;
    if (!tek) throw new Error('Pilih teknisi.');
    await svc.assignTeknisi(no, tek, (bg.querySelector('#a_note') as HTMLInputElement).value);
  }, done);
}

async function dlgDiagnose(no: string, done: () => void, onBack?: () => void): Promise<void> {
  let dugaan: string | null = null;
  try { dugaan = await repo.dugaanNama(no); } catch { /* dugaan opsional */ }
  const bg = openModal(`<div class="modal-head"><h3>Hasil Diagnosa</h3><button class="x">×</button></div>
    <div class="modal-body">
      ${dugaan ? `<div class="scope-note" style="margin-bottom:8px">💡 Dugaan awal CS: <b>${esc(dugaan)}</b></div>` : ''}
      <div class="field"><label>Estimasi biaya <span class="req">*</span></label><input id="g_est" value="Rp 0" inputmode="numeric"></div>
      <div class="field"><label>Estimasi lama</label><input id="g_lama" placeholder="mis. 2 hari"></div>
      <div class="field"><label>Keterangan (untuk CS)</label><textarea id="g_ket"></textarea></div>
      <label style="font-size:13px"><input type="checkbox" id="g_cancel"> Tidak bisa diperbaiki / batal</label>
      <div class="err"></div></div>${foot}`, { onBack });
  moneyInput(bg, 'g_est');
  wireSave(bg, async () => {
    const ket = (bg.querySelector('#g_ket') as HTMLTextAreaElement).value;
    if ((bg.querySelector('#g_cancel') as HTMLInputElement).checked) {
      if (!ket) throw new Error('Isi keterangan bila batal.');
      await svc.diagnoseCancel(no, ket); return;
    }
    const est = rpVal((bg.querySelector('#g_est') as HTMLInputElement).value);
    if (!est) throw new Error('Estimasi biaya wajib diisi.');
    await svc.diagnose(no, est, (bg.querySelector('#g_lama') as HTMLInputElement).value, ket);
  }, done);
}

async function dlgApprove(no: string, done: () => void, onBack?: () => void): Promise<void> {
  let full: Awaited<ReturnType<typeof repo.getTicket>>;
  try { full = await repo.getTicket(no); } catch (e) { toast((e as Error).message); return; }
  const t = full.ticket;
  const cust = (Array.isArray(t['customers']) ? (t['customers'] as { nama?: string; wa?: string }[])[0] : t['customers']) as { nama?: string; wa?: string } | null;
  const est = Number(t['est_biaya'] ?? 0), estLama = Number(t['est_biaya_lama'] ?? 0), dp = Number(t['dp'] ?? 0);
  const reconfirm = !!t['reconfirm'];
  const lama = t['lama'] ? String(t['lama']) : (t['est_selesai'] ? String(t['est_selesai']) : '—');
  const ket = t['ket'] ? String(t['ket']) : (t['kondisi'] ? String(t['kondisi']) : '—');
  const ketUlang = t['ket_ulang'] ? String(t['ket_ulang']) : '';
  const delta = est - estLama;
  const waNo = wa62(String(cust?.wa ?? ''));
  // Izin '📞 Lihat No. HP pelanggan' (Hak Akses) — bukan lagi hardcode peran, supaya Owner bisa mengatur.
  const canPhone = bisaBuka('pelanggan.telepon');
  const custNama = cust?.nama ?? 'pelanggan';
  let konfirmasi = !!t['konfirmasi'];

  const build = (): void => {
    const banner = reconfirm ? `<div class="panel" style="padding:10px;margin-bottom:10px;background:var(--warnbg);border:1px solid var(--warn);border-radius:8px">
        <div style="font-weight:700;color:var(--warn)">🔁 KONFIRMASI ULANG — jangan tertukar</div>
        <div style="font-size:13px;margin-top:4px">Konfirmasi lama (sudah disetujui): <b>${rp(estLama)}</b></div>
        <div style="font-size:13px">Biaya ${delta < 0 ? 'dikurangi' : 'tambahan'}: <b>${rp(Math.abs(delta))}</b> · Total baru: <b>${rp(est)}</b></div>
        ${ketUlang ? `<div style="font-size:13px">Alasan: ${esc(ketUlang)}</div>` : ''}</div>` : '';
    const info = `<div class="panel" style="padding:10px;margin-bottom:10px;background:var(--ice);border-radius:8px">
        <div style="font-size:13px">Estimasi biaya: <b>${rp(est)}</b>${reconfirm ? ' (sudah termasuk tambahan)' : ''}${dp > 0 ? ` · DP <b>${rp(dp)}</b> · Sisa <b>${rp(est - dp)}</b>` : ''}</div>
        <div style="font-size:13px">Estimasi waktu: ${esc(lama)}</div>
        <div style="font-size:13px">Keterangan teknisi: ${esc(ket)}</div></div>`;
    const waRow = canPhone ? `<div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:8px">
        <button class="btn" id="ap_wa" type="button">💬 Kirim WA minta persetujuan</button>
        ${konfirmasi
          ? '<button class="btn" id="ap_unkonf" type="button">↩️ Batal konfirmasi</button>'
          : '<button class="btn" id="ap_konf" type="button">✅ Tandai sudah konfirmasi</button>'}</div>` : '';
    const konfLine = `<div class="scope-note" style="margin-bottom:6px">Status konfirmasi: <b style="color:${konfirmasi ? 'var(--ok)' : 'var(--warn)'}">${konfirmasi ? 'Sudah konfirmasi' : 'Belum konfirmasi'}</b></div>`;
    const bg = openModal(`<div class="modal-head"><h3>Konfirmasi Pelanggan</h3><button class="x">×</button></div>
      <div class="modal-body">${banner}${info}${waRow}${konfLine}
        <div class="field"><label>Keputusan pelanggan</label>
          <label style="display:block"><input type="radio" name="ap" value="ya" checked> Setuju → dikerjakan</label>
          <label style="display:block"><input type="radio" name="ap" value="tidak"> Tidak setuju → bisa diambil</label></div>
        <div class="field"><label>Keterangan <span style="color:var(--muted);font-weight:400">(wajib bila tidak setuju)</span></label><textarea id="ap_ket"></textarea></div>
        <div class="err"></div></div>${foot}`, { onBack });
    (bg.querySelector('#ap_wa') as HTMLElement | null)?.addEventListener('click', () => {
      // WA hanya MEMBUKA WhatsApp (v0.18: "belum dianggap disetujui pelanggan"). Konfirmasi ditandai manual & eksplisit.
      if (waNo) openWA(waNo, fillTpl(waTpl().persetujuan, { nama: custNama, no, unit: String(t['unit'] ?? ''), merk: String(t['merk'] ?? ''), tipe: String(t['model'] ?? ''), kerusakan: String(t['keluhan'] ?? ''), est: rp(est), lama, ket }));
      else toast('Nomor WA pelanggan belum ada.');
    });
    (bg.querySelector('#ap_konf') as HTMLElement | null)?.addEventListener('click', async () => {
      try { await svc.setKonfirmasi(no, true); konfirmasi = true; bg.remove(); build(); } catch (e) { toast((e as Error).message); }
    });
    (bg.querySelector('#ap_unkonf') as HTMLElement | null)?.addEventListener('click', async () => {
      try { await svc.setKonfirmasi(no, false); konfirmasi = false; bg.remove(); build(); } catch (e) { toast((e as Error).message); }
    });
    wireSave(bg, async () => {
      const ya = (bg.querySelector('input[name=ap]:checked') as HTMLInputElement).value === 'ya';
      const ketv = (bg.querySelector('#ap_ket') as HTMLTextAreaElement).value;
      if (!ya && !ketv) throw new Error('Isi keterangan bila tidak setuju.');
      await svc.approve(no, ya, ketv);
    }, done);
  };
  build();
}

async function dlgFinish(no: string, curModal: number, outlet: string, done: () => void, onBack?: () => void): Promise<void> {
  let full: Awaited<ReturnType<typeof repo.getTicket>>;
  try { full = await repo.getTicket(no); } catch (e) { toast((e as Error).message); return; }
  const t = full.ticket;
  const cust = (Array.isArray(t['customers']) ? (t['customers'] as { kat?: string }[])[0] : t['customers']) as { kat?: string } | null;
  const kat: 'toko' | 'user' = cust?.kat === 'Toko' ? 'toko' : 'user';
  const est = Number(t['est_biaya'] ?? 0);
  const dp = Number(t['dp'] ?? 0);
  const biayaInit = Number(t['biaya'] ?? 0) || est;
  const curGar = t['garansi'] ? String(t['garansi']) : 'Tidak Ada';
  let jasa: Awaited<ReturnType<typeof repo.listJasaPrices>> = [];
  try { jasa = await repo.listJasaPrices(outlet); } catch { /* daftar jasa opsional */ }
  // %-jasa: harga = markup dari modal sparepart tiket (curModal). Rp-jasa: harga tetap.
  const jasaOpts = jasa.map((j) => {
    const ht = kat === 'toko' ? j.harga_type_toko : j.harga_type_user; // satuan per-field, ikut kategori pelanggan tiket
    const nilai = kat === 'toko' ? j.toko : j.user;
    const harga = jasaSellPrice(ht, nilai, curModal);
    const lbl = ht === 'persen' ? `${nilai}% sparepart = ${rp(harga)}` : `${rp(nilai)} + sparepart = ${rp(harga)}`;
    return `<option value="${j.id}" data-h="${harga}" data-ht="${ht}" data-n="${esc(j.nama)}">${esc(j.nama)} — ${lbl}</option>`;
  }).join('');
  // Servis ulang: sesi lama (round terakhir) → panel + pewarisan garansi (port kerjakanDialog v0.18).
  const prev = full.rounds.length ? full.rounds[full.rounds.length - 1] : null;
  const prevGar = prev?.garansi ?? 'Tidak Ada', prevBase = prev?.garansiBase ?? null;
  const punyaGarLama = !!prev && garansiDays(prevGar) > 0;
  const garWarisSisa = punyaGarLama ? garansiStatus(prevGar, prevBase).sisa : 0;
  const sesiNo = Number(t['ronde'] ?? 1);
  const paidNow = prev ? await repo.paidSoFarOf(no) : 0; // default usul refund = kas dibayar lintas sesi
  const prevPanel = prev ? `<div class="panel" style="padding:10px;margin-bottom:8px;background:var(--warnbg);border:1px solid var(--warn);border-radius:8px">
      <div style="font-weight:700;color:var(--warn)">🔁 Servis Ulang — sesi ${sesiNo}</div>
      <div style="font-size:13px;margin-top:3px">Biaya servis lama (sesi ${prev.ronde}): <b>${rp(Number(prev.biaya ?? 0))}</b></div>
      <div style="font-size:13px">Garansi lama: <b>${esc(prevGar)}</b>${garWarisSisa > 0 ? ` · sisa <b>${garWarisSisa} hari</b>` : (punyaGarLama ? ' · <span style="color:var(--danger)">habis</span>' : '')}</div>
      <div style="font-size:12px;color:var(--muted);margin-top:3px">Biaya baru default <b>Rp 0 (FREE)</b> — isi hanya bila ada biaya tambahan (lewat Konfirmasi Ulang).</div></div>` : '';
  const warisRow = punyaGarLama
    ? `<label style="display:flex;gap:6px;align-items:center;font-size:13px;margin:0 0 6px"><input type="checkbox" id="f_garwaris" checked> Lanjutkan garansi lama (${esc(prevGar)}${garWarisSisa > 0 ? ', sisa ' + garWarisSisa + ' hari' : ''}) — tidak reset</label>`
    : '';

  const bg = openModal(`<div class="modal-head"><h3>Hasil Pengerjaan — ${esc(no)}</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="scope-note" style="margin-bottom:8px">Tandai hasil akhir. Untuk tunda menunggu sparepart, pakai tombol Hold di detail.</div>
      ${prevPanel}
      <div class="field"><label>Hasil pengerjaan</label>
        <label style="display:flex;gap:6px;align-items:center;margin:4px 0"><input type="radio" name="fh" value="jadi" checked> Berhasil → Siap Diambil</label>
        <label style="display:flex;gap:6px;align-items:center"><input type="radio" name="fh" value="gagal"> Tidak bisa diperbaiki / batal → Siap Diambil</label></div>
      <div class="field" id="f_tindakanbox"><label>✅ Tindakan yang dikerjakan <span class="req">*</span></label>
        <textarea id="f_tindakan" placeholder="mis. ganti LCD, ganti konektor">${esc(String(t['tindakan'] ?? ''))}</textarea>
        <select id="f_addjasa" style="width:100%;font-size:12px;margin-top:5px"><option value="">➕ Tambah dari daftar Jasa (isi tindakan + biaya otomatis)…</option>${jasaOpts}</select>
        <div style="font-size:11px;color:var(--muted);margin-top:2px">Pilih jasa → nama + harga (${kat === 'toko' ? 'Toko' : 'User'}) ditambahkan otomatis. Bisa diedit.</div></div>
      <div class="row2"><div class="field"><label>Biaya (Rp) <span style="color:var(--muted);font-weight:400">— ke pelanggan</span></label><input id="f_biaya" value="${rpFmt(String(biayaInit))}" inputmode="numeric"></div>
        <div class="field"><label>Modal Sparepart (Rp) ${curModal > 0 ? '<span style="color:var(--muted);font-weight:400">(otomatis)</span>' : ''}</label><input id="f_modal" value="${rpFmt(String(curModal))}" inputmode="numeric">${curModal > 0 ? `<div style="font-size:11px;color:var(--ok);margin-top:2px">✔ dari sparepart supplier: <b>${rp(curModal)}</b></div>` : ''}</div></div>
      <div class="scope-note" style="margin:-2px 0 6px">Profit = Biaya − Modal (laba-rugi).${dp > 0 ? ` · DP <b>${rp(dp)}</b> · sisa <b id="f_sisa">${rp(Math.max(0, biayaInit - dp))}</b>` : ''}</div>
      <div class="field" id="f_garbox"><label>Masa Garansi <span style="color:var(--muted);font-weight:400">(bisa di-override CS saat pengambilan)</span></label>${warisRow}<select id="f_gar" ${punyaGarLama ? 'disabled' : ''}>${GARANSI_OPTS.map((g) => `<option ${g === curGar ? 'selected' : ''}>${esc(g)}</option>`).join('')}</select></div>
      <div class="field" id="f_ketbox" style="display:none"><label>❌ Keterangan gagal — alasan tidak bisa diperbaiki <span class="req">*</span></label><textarea id="f_ket" placeholder="wajib bila tidak bisa diperbaiki"></textarea></div>
      ${prev ? `<div class="panel" id="f_refundbox" style="display:none;padding:10px;margin:6px 0;background:var(--dangerbg);border:1px solid var(--danger);border-radius:8px">
        <div style="font-weight:700;color:var(--danger)">💸 Usul Pengembalian Dana — servis ulang sesi ${sesiNo} gagal</div>
        <div style="font-size:12px;color:var(--muted);margin:2px 0 6px">Unit servis ulang gagal diperbaiki. Usulkan dana yang perlu dikembalikan; diproses & diverifikasi Finance saat serah terima.</div>
        <div class="field"><label>Jumlah usul dikembalikan (Rp)</label><input id="f_refund" value="${rpFmt(String(paidNow))}" inputmode="numeric"></div>
        <div class="field"><label>Catatan</label><input id="f_refundnote" placeholder="opsional"></div></div>` : ''}
      <div class="err"></div></div>${foot}`, { onBack });

  const biayaEl = bg.querySelector('#f_biaya') as HTMLInputElement;
  moneyInput(bg, 'f_biaya'); moneyInput(bg, 'f_modal'); moneyInput(bg, 'f_refund');
  const sisaEl = bg.querySelector('#f_sisa');
  const updSisa = (): void => { if (sisaEl) sisaEl.textContent = rp(Math.max(0, rpVal(biayaEl.value) - dp)); };
  biayaEl.addEventListener('input', updSisa);
  const toggle = (): void => {
    const gagal = (bg.querySelector('input[name=fh]:checked') as HTMLInputElement).value === 'gagal';
    (bg.querySelector('#f_tindakanbox') as HTMLElement).style.display = gagal ? 'none' : '';
    (bg.querySelector('#f_garbox') as HTMLElement).style.display = gagal ? 'none' : '';
    (bg.querySelector('#f_ketbox') as HTMLElement).style.display = gagal ? '' : 'none';
    const rb = bg.querySelector('#f_refundbox') as HTMLElement | null; // usul refund hanya saat gagal & ada sesi lama
    if (rb) rb.style.display = gagal ? '' : 'none';
    if (gagal) { biayaEl.value = 'Rp 0'; updSisa(); }
    else if (rpVal(biayaEl.value) === 0) { biayaEl.value = rpFmt(String(est)); updSisa(); }
  };
  bg.querySelectorAll('input[name=fh]').forEach((r) => r.addEventListener('change', toggle));
  const warisCb = bg.querySelector('#f_garwaris') as HTMLInputElement | null;
  warisCb?.addEventListener('change', () => { (bg.querySelector('#f_gar') as HTMLSelectElement).disabled = warisCb.checked; });
  const addSel = bg.querySelector('#f_addjasa') as HTMLSelectElement;
  let jasaPartCounted = false; // modal sparepart hanya masuk biaya SEKALI (jasa pertama) → cegah double-count saat >1 jasa
  addSel.addEventListener('change', () => {
    const opt = addSel.selectedOptions[0]; if (!opt || !opt.value) return;
    const harga = Number(opt.dataset.h ?? 0), nm = opt.dataset.n ?? '';
    // %-jasa tanpa modal sparepart → harga 0 (menyesatkan). Beri tahu; nama tetap ditambah, biaya bisa diisi manual.
    if (opt.dataset.ht === 'persen' && curModal <= 0) toast('⚠️ Modal sparepart belum ada — harga %-jasa = Rp 0. Rekam sparepart dulu / isi biaya manual.');
    // Jasa 1 = sparepart+biaya penuh; jasa berikutnya = biaya (labor) saja (harga−modal) agar part tak terhitung ganda.
    const add = jasaPartCounted ? Math.max(0, harga - curModal) : harga;
    jasaPartCounted = true;
    const tb = bg.querySelector('#f_tindakan') as HTMLTextAreaElement;
    tb.value = (tb.value.trim() ? tb.value.trim() + ', ' : '') + nm;
    biayaEl.value = rpFmt(String(rpVal(biayaEl.value) + add)); updSisa();
    addSel.value = '';
  });
  wireSave(bg, async () => {
    const jadi = (bg.querySelector('input[name=fh]:checked') as HTMLInputElement).value === 'jadi';
    const tindakan = (bg.querySelector('#f_tindakan') as HTMLTextAreaElement).value.trim();
    const ket = (bg.querySelector('#f_ket') as HTMLTextAreaElement).value.trim();
    if (jadi && !tindakan) throw new Error('Isi tindakan yang dikerjakan.');
    if (!jadi && !ket) throw new Error('Isi keterangan gagal.');
    // Pewarisan garansi: waris → pakai garansi & base sesi lama (tak reset); fresh → dari select, base null (pickup yg set).
    const waris = !!warisCb?.checked;
    const garansiVal = waris ? prevGar : (bg.querySelector('#f_gar') as HTMLSelectElement).value;
    const garansiBaseVal = (jadi && waris) ? prevBase : null;
    // Servis ulang gagal → usul refund (default kas lintas sesi, editable teknisi).
    const refundUsul = (!jadi && prev)
      ? { amount: rpVal((bg.querySelector('#f_refund') as HTMLInputElement).value), note: (bg.querySelector('#f_refundnote') as HTMLInputElement).value.trim(), ronde: sesiNo }
      : null;
    await svc.finishWork(no, jadi,
      rpVal(biayaEl.value), rpVal((bg.querySelector('#f_modal') as HTMLInputElement).value),
      tindakan, garansiVal, ket, garansiBaseVal, refundUsul);
  }, done);
}

const GARANSI_OPTS = ['Tidak Ada', '3 Hari', '7 Hari', '14 Hari', '30 Hari', '1 Bulan', '2 Bulan', '3 Bulan', '6 Bulan', '1 Tahun', 'Lifetime'];

interface PickupCtx {
  no: string; custNama: string; unit: string;
  /** Cabang tiket — menentukan akun kas mana yang boleh dipilih (laci & rekening tiap cabang beda). */
  outlet?: string | null;
  svcAmt: number; dpAmt: number; fee: { readyDays: number; fee: number }; totalAmt: number; sisaAmt: number;
  draft: { nama: string; cara: string; lunas: boolean; jatuhTempo: string | null; garansi: string };
}
type Money = Pick<PickupCtx, 'svcAmt' | 'dpAmt' | 'fee' | 'totalAmt' | 'sisaAmt'>;

const money2 = (lbl: string, val: string, style = ''): string =>
  `<div style="display:flex;justify-content:space-between;${style}"><span>${lbl}</span><b>${val}</b></div>`;
function rincianPanel(c: Money): string {
  return `<div class="scope-note" style="border:1px solid var(--line);border-radius:8px;padding:8px 10px">
    ${money2('Biaya servis', rp(c.svcAmt))}
    ${c.fee.fee > 0 ? money2(`(+) Biaya simpan (telat ${c.fee.readyDays} hr)`, rp(c.fee.fee), 'color:var(--warn)') : ''}
    ${money2('Total', rp(c.totalAmt), 'border-top:1px dashed var(--line);margin-top:4px;padding-top:4px')}
    ${c.dpAmt > 0 ? money2('(−) DP', rp(c.dpAmt)) : ''}
    ${money2('<b>Sisa dibayar</b>', `<span style="font-size:16px">${rp(c.sisaAmt)}</span>`, 'color:var(--navy)')}
  </div>`;
}

/** Jatuh tempo default piutang — dari Pengaturan (Kebijakan → tempoHari), bukan angka mati. */
function jatuhTempoDefault(): string {
  const d = new Date(); d.setDate(d.getDate() + Math.max(0, kebijakan().tempoHari || 14));
  return isoOf(d);
}

/** Langkah 1 — Serah Terima (kumpulkan data; BELUM simpan). Port ambilDialog v0.18. */
export async function dlgPickup(no: string, done: () => void, onBack?: () => void): Promise<void> {
  const due14 = jatuhTempoDefault();
  let fee = { readyDays: 0, fee: 0 };
  try { fee = await svc.storageFeePreview(no); } catch { /* fee opsional */ }
  let full: Awaited<ReturnType<typeof repo.getTicket>>;
  try { full = await repo.getTicket(no); } catch (e) { toast((e as Error).message); return; }
  const t = full.ticket;
  const hasil = t['hasil'] ? String(t['hasil']) : '';
  const svcAmt = hasil ? Number(t['biaya'] ?? 0) : (Number(t['biaya'] ?? 0) || Number(t['est_biaya'] ?? 0));
  const dpAmt = Number(t['dp'] ?? 0);
  const totalAmt = svcAmt + fee.fee, sisaAmt = Math.max(0, totalAmt - dpAmt);
  const kondisiTxt = String(t['kondisi'] ?? '').trim();
  const curGar = t['garansi'] ? String(t['garansi']) : 'Tidak Ada';
  const hasRefund = !!t['refund'];
  const p = profil();
  const outletTiket = (t['outlet'] as string | null) ?? null;
  const ctxBase: Omit<PickupCtx, 'draft'> = { no, custNama: full.custNama, unit: String(t['unit'] ?? ''), outlet: outletTiket, svcAmt, dpAmt, fee, totalAmt, sisaAmt };

  const refundInline = (hasil === 'tidakbisa' || hasil === 'batal')
    ? `<div class="panel" style="background:var(--dangerbg);border-radius:8px;padding:8px 10px;margin-bottom:8px">
        <div style="color:var(--danger);font-weight:600">Servis ${hasil === 'batal' ? 'dibatalkan' : 'tidak bisa diperbaiki'}.</div>
        ${hasRefund
          ? '<div style="font-size:12px;color:var(--danger);margin-top:2px">↩️ Pengembalian dana sudah diajukan — menunggu verifikasi Finance.</div>'
          : '<div style="font-size:12px;margin:4px 0">Bila ada uang masuk / hutang tempo yang harus dikembalikan:</div><button class="btn" id="p_refund" style="background:var(--danger);color:#fff;border-color:var(--danger);padding:4px 10px;font-size:12px">💸 Proses Pengembalian Dana</button>'}
      </div>` : '';

  const bg = openModal(`<div class="modal-head"><h3>Serah Terima — ${esc(no)}</h3><button class="x">×</button></div>
    <div class="modal-body">
      ${refundInline}
      ${kondisiTxt ? `<div class="scope-note">Kondisi: ${esc(kondisiTxt)} · Garansi: ${esc(curGar)}</div>` : ''}
      <div class="field"><label>Diambil oleh <span class="req">*</span></label><input id="p_nama" value="${esc(full.custNama)}"></div>
      <div class="field"><label>Cara bayar</label><select id="p_cara">${caraBayarOpsi(outletTiket).map((c) => `<option>${esc(c)}</option>`).join('')}</select></div>
      <div id="p_bayarinfo" class="scope-note" style="font-size:12px"></div>
      <div class="field" id="p_tempobox" style="display:none"><label>Jatuh tempo</label><input id="p_tempo" type="date" value="${due14}"></div>
      <div class="field"><label>Masa Garansi</label><select id="p_gar">${GARANSI_OPTS.map((g) => `<option ${g === curGar ? 'selected' : ''}>${esc(g)}</option>`).join('')}</select></div>
      <div id="p_fotounit"></div>
      ${rincianPanel(ctxBase)}
      ${fee.fee > 0 ? '<div class="scope-note" style="color:var(--warn);font-size:12px">Biaya simpan = pendapatan lain-lain (BUKAN omzet servis).</div>' : ''}
      ${(() => { const rl = currentUser()?.role; return rl && canMarkLunas(rl); })() ? '<label style="font-size:13px" id="p_lunasbox"><input type="checkbox" id="p_lunas" checked> Lunas (CS tandai; Finance verifikasi)</label>' : '<div class="scope-note" style="font-size:11px" id="p_lunasbox">Anda tak berwenang menandai lunas — serah unit tetap bisa; CS menandai lunas di Status Pembayaran.</div>'}
      <div class="scope-note" style="font-size:11px">Status pindah ke "Sudah Diambil" setelah Nota di-Print / WA / Simpan pada langkah berikutnya.</div>
      <div class="err"></div></div>
    <div class="modal-foot"><button class="btn" id="p_batal">Batal</button>
      <button class="btn" id="p_kuasa">📝 Diwakilkan</button>
      <button class="btn primary" id="p_next">Lanjut → Konfirmasi Pembayaran</button></div>`, { onBack });

  // Foto saat unit diserahkan — pasangan foto saat diterima. Satu foto sudah cukup: yang
  // dipersoalkan biasanya sisi yang lecet, dan CS tak akan memotret empat sisi sambil melayani.
  const fotoSerah = fotoUnitPicker(bg.querySelector('#p_fotounit') as HTMLElement,
    { outlet: outletTiket, fase: 'serah', oleh: currentUser()?.nama ?? '', maks: 2 });
  const cara = bg.querySelector('#p_cara') as HTMLSelectElement;
  const tempoBox = bg.querySelector('#p_tempobox') as HTMLElement;
  const lunasBox = bg.querySelector('#p_lunasbox') as HTMLElement;
  const info = bg.querySelector('#p_bayarinfo') as HTMLElement;
  const syncCara = (): void => {
    const v = cara.value, tempo = isPiutangCara(v);
    tempoBox.style.display = tempo ? '' : 'none';
    lunasBox.style.display = tempo ? 'none' : '';
    info.innerHTML = v === 'Transfer' ? `Rekening: <b>${esc(p.rekening)}</b>`
      : v === 'QRIS' ? esc(p.qris)
      : tempo ? `Sisa tagihan masuk <b>Piutang</b> — ditagih kemudian.${wajibJatuhTempo(v) ? '' : ' Jatuh tempo opsional.'}` : '';
  };
  cara.addEventListener('change', syncCara); syncCara();
  const rePickup = (): void => void dlgPickup(no, done, onBack); // ← Back sub-dialog serah → buka lagi Serah Terima (re-arm onBack ke detail)
  bg.querySelector('#p_batal')?.addEventListener('click', () => bg.remove());
  bg.querySelector('#p_refund')?.addEventListener('click', () => { bg.remove(); void openRefundDialog(no, done, rePickup); });
  // Serah terima diwakilkan: setelah surat kuasa jadi, alurnya BELUM selesai — pembayaran
  // masih harus diselesaikan. Tanpa tombol ini petugas menutup pratinjau lalu mengira sudah
  // beres, dan unit terlanjur diserahkan tanpa pembayaran tercatat.
  bg.querySelector('#p_kuasa')?.addEventListener('click', () => {
    bg.remove();
    void previewSuratKuasa(no, rePickup, { label: '💳 Lanjut ke Pembayaran →', aksi: rePickup });
  });
  bg.querySelector('#p_next')?.addEventListener('click', () => {
    const nama = (bg.querySelector('#p_nama') as HTMLInputElement).value.trim();
    const errEl = bg.querySelector('.err') as HTMLElement;
    if (!nama) { errEl.textContent = 'Isi nama pengambil.'; return; }
    const tempo = isPiutangCara(cara.value);
    const draft = {
      nama, cara: cara.value,
      lunas: !!(bg.querySelector('#p_lunas') as HTMLInputElement | null)?.checked, // MENTAH — cara final baru diputus di langkah Konfirmasi (null utk peran tak-boleh-markLunas, mis. SPV)
      jatuhTempo: tempo ? ((bg.querySelector('#p_tempo') as HTMLInputElement).value || null) : null,
      garansi: (bg.querySelector('#p_gar') as HTMLSelectElement).value,
    };
    // Foto disimpan di sini, bukan setelah pembayaran: buktinya tak boleh hilang kalau petugas
    // mundur di langkah berikutnya. Gagal menyimpan tak boleh menghalangi serah unit.
    const fs = fotoSerah.nilai().filter((f) => f.fase === 'serah');
    if (fs.length) void repo.tambahFotoUnit(no, fs).catch((e: Error) => toast('Foto gagal disimpan: ' + e.message));
    bg.remove(); dlgKonfirmasiBayar({ ...ctxBase, draft }, done, rePickup); // ← header Konfirmasi = kembali ke Serah Terima
  });
}

/** Langkah 2 — Konfirmasi Pembayaran (CS verifikasi kas diterima). Port konfirmasiBayarDialog v0.18. */
function dlgKonfirmasiBayar(c: PickupCtx, done: () => void, onBack?: () => void): void {
  const due14 = jatuhTempoDefault();
  const prof = profil(); // rekening/QRIS ditampilkan sesuai metode yang dipilih
  const bg = openModal(`<div class="modal-head"><h3>Konfirmasi Pembayaran — ${esc(c.no)}</h3><button class="x">×</button></div>
    <div class="modal-body">
      ${rincianPanel(c)}
      <div class="field" style="margin-top:10px"><label>Uang diterima via <span class="req">*</span></label>
        <select id="k_cara">${caraBayarOpsi(c.outlet ?? null).map((x) => `<option ${x === c.draft.cara ? 'selected' : ''}>${esc(x)}</option>`).join('')}</select>
        <div class="scope-note" id="k_info" style="font-size:12px"></div></div>
      <div class="field" id="k_tempobox" style="display:none"><label>Jatuh tempo <span id="k_tempoopt" style="font-weight:400;color:var(--muted)"></span></label>
        <input id="k_tempo" type="date" value="${esc(c.draft.jatuhTempo ?? due14)}"></div>
      <div class="scope-note" id="k_ring" style="margin-top:8px"></div>
      <div class="err"></div></div>
    <div class="modal-foot">
      <button class="btn primary" id="k_ok">✅ Konfirmasi Pembayaran</button></div>`, { onBack }); // ← header = kembali ke Serah Terima

  // Metode dikonfirmasi DI SINI — inilah saat uang benar-benar berpindah tangan. Yang dipilih di
  // langkah Serah Terima hanya rencana; kenyataannya bisa berubah di kasir (mis. QRIS gagal → tunai).
  // Pilihan ini yang dipakai Tutup Kasir untuk memisahkan uang laci dan uang bank secara otomatis.
  const cara = bg.querySelector('#k_cara') as HTMLSelectElement;
  const tempoBox = bg.querySelector('#k_tempobox') as HTMLElement;
  const tempoOpt = bg.querySelector('#k_tempoopt') as HTMLElement;
  const info = bg.querySelector('#k_info') as HTMLElement;
  const ring = bg.querySelector('#k_ring') as HTMLElement;
  const sync = (): void => {
    const v = cara.value, piutang = isPiutangCara(v), wajib = wajibJatuhTempo(v);
    tempoBox.style.display = piutang ? '' : 'none';
    tempoOpt.textContent = wajib ? '(wajib)' : '(opsional)';
    info.innerHTML = v === 'Transfer' ? `Rekening: <b>${esc(prof.rekening)}</b>` : v === 'QRIS' ? esc(prof.qris) : '';
    ring.innerHTML = piutang
      ? `Pengambil: <b>${esc(c.draft.nama)}</b> · <b>${esc(v)}</b> — sisa <b>${rp(c.sisaAmt)}</b> masuk <b>Piutang</b>, ditagih kemudian.`
      : `CS memverifikasi: pelanggan membayar <b>${rp(c.sisaAmt)}</b> sekarang via <b>${esc(v)}</b>.${c.draft.lunas ? ' Ditandai LUNAS.' : ''}`;
  };
  cara.addEventListener('change', sync); sync();

  const reKonf = (): void => dlgKonfirmasiBayar(c, done, onBack); // re-arm: ← Nota → buka lagi Konfirmasi (chain utuh)
  bg.querySelector('#k_ok')?.addEventListener('click', () => {
    const v = cara.value, piutang = isPiutangCara(v);
    const jt = (bg.querySelector('#k_tempo') as HTMLInputElement).value || null;
    if (wajibJatuhTempo(v) && !jt) { (bg.querySelector('.err') as HTMLElement).textContent = 'Isi tanggal jatuh tempo untuk Tempo (Toko).'; return; }
    // Metode di sini yang menang atas pilihan langkah sebelumnya; piutang tak pernah "lunas".
    const draft = { ...c.draft, cara: v, lunas: lunasFinal(c.draft.lunas, v), jatuhTempo: piutang ? jt : null };
    bg.remove(); dlgNota({ ...c, draft }, done, reKonf);
  });
}

/** Langkah 3 — Nota (GATING: status pindah HANYA setelah Print/WA/Simpan). Port notaDialog v0.18. */
function dlgNota(c: PickupCtx, done: () => void, onBack?: () => void): void {
  const bg = openModal(`<div class="modal-head"><h3>Nota Servis — ${esc(c.no)}</h3><button class="x">×</button></div>
    <div class="modal-body"><div class="scope-note">Selesaikan serah terima. Pilih salah satu — tiket pindah ke <b>Sudah Diambil</b> setelahnya:</div><div class="err"></div></div>
    <div class="modal-foot" style="flex-wrap:wrap">
      <button class="btn primary" id="n_print">🖨️ Cetak Nota</button>
      <button class="btn" id="n_wa">💬 Kirim WA</button>
      <button class="btn" id="n_skip">Simpan tanpa cetak</button></div>`, { onBack });
  const finalize = async (): Promise<boolean> => {
    try { await svc.pickup(c.no, c.draft.cara, c.draft.lunas, c.draft.nama, c.draft.jatuhTempo, c.draft.garansi); return true; }
    catch (e) { const el = bg.querySelector('.err') as HTMLElement; el.textContent = (e as Error).message; return false; }
  };
  const after = (): void => { toast('✅ Diserahkan'); bg.remove(); done(); refreshCounts(); };
  // Guard anti double-fire: kunci semua tombol saat proses; pickup dijamin dipanggil SEKALI.
  let busy = false;
  const run = async (extra: () => void): Promise<void> => {
    if (busy) return; busy = true;
    const btns = Array.from(bg.querySelectorAll('button')) as HTMLButtonElement[];
    btns.forEach((b) => { b.disabled = true; });
    if (await finalize()) { after(); extra(); }
    else { busy = false; btns.forEach((b) => { b.disabled = false; }); }
  };
  bg.querySelector('#n_print')?.addEventListener('click', () => void run(() => void previewNota(c.no)));
  bg.querySelector('#n_wa')?.addEventListener('click', async () => {
    const r = await serahWAPesan(c.no, c.draft.garansi);
    if (!r) { toast('Nomor WA pelanggan belum ada.'); return; }
    void run(() => openWA(r.wa, r.pesan));
  });
  bg.querySelector('#n_skip')?.addEventListener('click', () => void run(() => { /* simpan tanpa cetak */ }));
}

/**
 * Pesan WA GABUNGAN ke pelanggan saat serah / di Sudah Diambil:
 * terima kasih + link survei + kartu garansi + follow sosmed. Dipakai dlgNota (Bisa Diambil)
 * DAN tombol WA di Sudah Diambil. Return null bila pelanggan tak punya nomor WA.
 */
export async function serahWAPesan(no: string, garansiTxt: string | null): Promise<{ wa: string; pesan: string } | null> {
  const ct = await getTicketContact(no);
  const w = wa62(ct.wa);
  if (!w) return null;
  const survei = ct.token ? `${alamatPublik()}/survei.html#t=${ct.token}` : '(scan QR survei di Nota)';
  const garansiLink = ct.token ? `${garansiBase()}${ct.token}` : '';
  const p = profil();
  const sosmed = (p.sosmedHub?.trim())
    || [p.instagram && `IG ${p.instagram}`, p.tiktok && `TikTok ${p.tiktok}`, p.facebook && `FB ${p.facebook}`].filter(Boolean).join(' · ')
    || '-';
  const garansi = [garansiTxt?.trim() || '-', garansiLink ? `(kartu: ${garansiLink})` : ''].filter(Boolean).join(' ');
  const pesan = fillTpl(waTpl().serah, { nama: ct.nama, no, unit: ct.unit, toko: p.namaUsaha, garansi, survei, sosmed, tagline: p.tagline });
  return { wa: w, pesan };
}

function dlgPrioritas(no: string, cur: string, done: () => void, onBack?: () => void): void {
  const isOM = ['Owner', 'Manager'].includes(currentUser()?.role ?? ''); // P0 (darurat) khusus O/M — port v0.18
  const opts = (isOM ? ['P0'] : []).concat(['P1', 'P2', 'P3']);
  const bg = openModal(`<div class="modal-head"><h3>⚡ Set Prioritas</h3><button class="x">×</button></div>
    <div class="modal-body"><div class="field"><label>Prioritas</label><select id="pr_sel">${opts.map((p) => `<option ${p === cur ? 'selected' : ''}>${p}</option>`).join('')}</select></div><div class="err"></div></div>${foot}`, { onBack });
  wireSave(bg, async () => { await svc.setPrioritas(no, (bg.querySelector('#pr_sel') as HTMLSelectElement).value); }, done);
}

function dlgReconfirm(no: string, oldEst: number, done: () => void, onBack?: () => void): void {
  const bg = openModal(`<div class="modal-head"><h3>🔁 Konfirmasi Ulang — Biaya Tambahan</h3><button class="x">×</button></div>
    <div class="modal-body">
      <div class="scope-note">Biaya disetujui saat ini: <b>${rp(oldEst)}</b></div>
      <div class="field"><label>Jenis</label>
        <label style="display:block"><input type="radio" name="rc" value="tambah" checked> Tambah biaya</label>
        <label style="display:block"><input type="radio" name="rc" value="kurang"> Kurangi biaya</label></div>
      <div class="field"><label>Nominal <span class="req">*</span> <span style="color:var(--muted);font-weight:400">(tambahan/pengurangan)</span></label><input id="rc_nom" value="Rp 0" inputmode="numeric"></div>
      <div class="field"><label>Alasan <span class="req">*</span></label><textarea id="rc_ket"></textarea></div><div class="err"></div></div>${foot}`, { onBack });
  moneyInput(bg, 'rc_nom');
  wireSave(bg, async () => {
    const jenis: 'tambah' | 'kurang' = (bg.querySelector('input[name=rc]:checked') as HTMLInputElement).value === 'kurang' ? 'kurang' : 'tambah';
    const nom = rpVal((bg.querySelector('#rc_nom') as HTMLInputElement).value);
    const ket = (bg.querySelector('#rc_ket') as HTMLTextAreaElement).value;
    if (!nom) throw new Error('Nominal wajib diisi.');
    if (!ket) throw new Error('Alasan wajib diisi.');
    await svc.reconfirm(no, jenis, nom, oldEst, ket);
  }, done);
}
